import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { PaywallModal } from "@/components/PaywallModal";
import { SessionTimeoutModal } from "@/components/SessionTimeoutModal";
import { TrialBanner } from "@/components/TrialBanner";
import { AceCommandPalette } from "@/components/AceCommandPalette";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { useIsMobile } from "@/hooks/use-mobile";
import { LogOut, User, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const DashboardLayout = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const isMobile = useIsMobile();
  
  const rememberMe = localStorage.getItem('classplan_remember_me') === 'true';
  
  const { showWarning, timeRemaining, extendSession, logout } = useSessionTimeout({
    enabled: !rememberMe,
    warningMinutes: 25,
    timeoutMinutes: 30,
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", user.id)
        .single();
      setProfile(data);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success("Logged out successfully");
    navigate("/auth");
  };

  if (isMobile) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md text-center space-y-6">
          <img 
            src="/android-chrome-192x192.png" 
            alt="ClassPlan" 
            className="w-16 h-16 mx-auto"
          />
          
          <div className="space-y-3">
            <h1 className="text-xl font-semibold text-foreground">Desktop Access Required</h1>
            <p className="text-muted-foreground text-sm leading-relaxed">
              ClassPlan's dashboard is optimized for desktop use. 
              Please access from a computer for the best experience.
            </p>
          </div>
          
          <Button onClick={handleLogout} variant="outline" size="sm">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <PaywallModal />
      <AceCommandPalette />
      <SessionTimeoutModal 
        open={showWarning}
        timeRemaining={timeRemaining}
        onExtend={extendSession}
        onLogout={logout}
      />
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        
        <div className="flex-1 flex flex-col min-w-0">
          <TrialBanner />
          
          <header className="h-14 border-b border-border bg-background flex items-center justify-between px-6 sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
              
              {/* Command Palette Trigger */}
              <Button
                variant="outline"
                size="sm"
                className="hidden sm:flex items-center gap-2 text-muted-foreground h-8 px-3 w-64 justify-between"
                onClick={() => {
                  // Trigger the command palette via keyboard event
                  const event = new KeyboardEvent('keydown', {
                    key: 'k',
                    metaKey: true,
                    bubbles: true,
                  });
                  document.dispatchEvent(event);
                }}
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  <span className="text-sm">Ask Ace anything...</span>
                </div>
                <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </Button>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
                  <div className="h-7 w-7 rounded-full bg-accent flex items-center justify-center">
                    <User className="h-4 w-4" />
                  </div>
                  <span className="hidden sm:inline text-sm">
                    {profile?.full_name || "Account"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/account")}>
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          <main className="flex-1 overflow-y-auto bg-section-subtle">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};